// Phase 17 Plan 02 — GroupCancelModal (CXL-02: pre-confirm disclosure).
//
// Mirrors the STRUCTURE of CancelBookingModal (disclose → single destructive
// confirm → toast → invalidate) but is a NEW component for the event cancel
// surface. Events use deleteJoin (no tier percentage, binary 8h refund rule,
// group framing). See 17-RESEARCH §6.
//
// Threat mitigations:
//   T-17-02 — affected-group sentence sourced from useGroupCancel.names (display_name
//              only, never DOB); grep gate: no date_of_birth/age/toISOString references.
//   T-17-03 — refund figure sourced from hook disclosure (backend-authoritative
//              Σ total_cents + start_time); never recomputed in the modal.
//   T-17-04 — Confirm isLoading+isDisabled while pending; handleClose early-returns
//              mid-flight (no optimistic close, D-07/D-08).
//   T-17-05 — closed/error/already_cancelled each render explicit copy with Confirm
//              disabled; never a blank modal, never a guessed amount (D-11/D-12/D-13).

import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Skeleton,
  Text,
} from '@chakra-ui/react'
import { useGroupCancel } from '../../pages/user/useGroupCancel'
import type { GroupedBooking } from '../../pages/user/groupCancel'

// formatUsd copied verbatim from CancelBookingModal.tsx:29-30 (UI-SPEC mandatory reuse).
const formatUsd = (cents: number) =>
  (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' })

export interface GroupCancelModalProps {
  isOpen: boolean
  onClose: () => void
  group: GroupedBooking | null
  viewerMemberId: string | null
  /** Force a specific branch so wiring layer (Plan 17-03) can show backend-detected states. */
  state?: 'closed' | 'already_cancelled' | 'error'
}

const GroupCancelModal = ({ isOpen, onClose, group, viewerMemberId, state }: GroupCancelModalProps) => {
  const { disclosure, names, isPending, isError, confirmCancel } = useGroupCancel({
    group,
    viewerMemberId,
    onClose,
  })

  // D-08: no close mid-flight — guard both the overlay close and the dismiss button.
  const handleClose = () => {
    if (isPending) return
    onClose()
  }

  // Per-event grouping (#8) can span multiple bookings/checkouts; pluralize copy.
  const bookingWord = group && group.rows.length > 1 ? 'bookings' : 'booking'

  // Refund breakdown: money-paid rows refund as cash, class-pack rows refund as
  // a returned credit — disclose each so the user knows the form of the refund.
  const refundCents = disclosure?.refundCents ?? 0
  const creditCount = disclosure?.creditCount ?? 0
  const packLabel = `${creditCount} class pack${creditCount > 1 ? 's' : ''}`

  // Confirm is disabled for terminal/error states but stays ENABLED for the $0/within-window
  // case (refundable === false with no state override) — D-10: still allow no-refund cancel.
  const confirmDisabled =
    state === 'closed' || state === 'already_cancelled' || state === 'error' || isError || !group

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Cancel booking</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {/* Branch order follows 17-PATTERNS.md: override states first, then active disclosure,
              then $0, then loading skeleton (group==null). */}
          {state === 'closed' ? (
            <Text textStyle="medium.300">
              This booking can no longer be cancelled — the event has already started. Contact the
              venue for help.
            </Text>
          ) : state === 'already_cancelled' ? (
            <Text textStyle="medium.300">This booking is already cancelled.</Text>
          ) : state === 'error' || isError ? (
            <Text textStyle="medium.300" color="#FF3B30">
              Couldn't load your cancellation details. Try again.
            </Text>
          ) : disclosure?.refundable ? (
            <>
              {/* Per-event framing (#8): ONE deleteJoin cancels every booking the
                  member has for this event, across all checkouts — so we disclose
                  the whole event, the full refund, and all affected attendees. */}
              <Text textStyle="medium.400" mb="2">
                Cancel your {bookingWord}
                {group?.eventTitle ? ` for ${group.eventTitle}` : ''}?
              </Text>
              <Text textStyle="medium.300">
                You'll get back{' '}
                {refundCents > 0 && <b>{formatUsd(refundCents)}</b>}
                {refundCents > 0 && creditCount > 0 && ' and '}
                {creditCount > 0 && <b>{packLabel}</b>}
                {refundCents === 0 && creditCount === 0 && <b>{formatUsd(0)}</b>}.
              </Text>
              <Text textStyle="medium.300" mt="2">
                This cancels {names}.
              </Text>
            </>
          ) : disclosure && !disclosure.refundable ? (
            // $0 / within 8h window — D-10: honest disclosure, Confirm STAYS enabled.
            <>
              <Text textStyle="medium.400" mb="2">
                Cancel your {bookingWord}
                {group?.eventTitle ? ` for ${group.eventTitle}` : ''}?
              </Text>
              <Text textStyle="medium.300">This cancels {names}.</Text>
              <Text textStyle="light.300" color="#FF3B30" mt="2">
                It's within 8 hours of the start, so this cancellation isn't refundable — you'll
                get $0 back.
              </Text>
            </>
          ) : (
            // group == null — not yet resolved
            <Skeleton height="60px" />
          )}
        </ModalBody>
        <ModalFooter>
          {/* Dismiss: light gray pill, disabled while mutation is in-flight (D-07). */}
          <Button variant="light" mr={3} onClick={handleClose} isDisabled={isPending}>
            Keep booking
          </Button>
          {/* Confirm: heavy black pill (NOT red — UI-SPEC; LVBL uses copy for destructive signal).
              isLoading shows spinner; isDisabled guards terminal/error states only (D-10). */}
          <Button
            variant="heavy"
            onClick={confirmCancel}
            isLoading={isPending}
            isDisabled={confirmDisabled}
          >
            Cancel booking
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default GroupCancelModal
